import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { enqueueCartMutation } from '@theme/cart-queue';

/** Cart attribute listing the gift variants the shopper removed, comma separated (the underscore hides it at checkout) */
const DECLINED_ATTRIBUTE = '_declined_gifts';

/**
 * Keeps the subscription gifts in step with the cart.
 *
 * After every cart change: while any line carries a selling plan, the gift
 * variants — `Theme.gifts.variants` from theme settings, plus any the
 * product page's buy box publishes (`data-gift-variants` on
 * `pdp-buy-box-component`) — are added once each, tagged with a hidden
 * `_gift` line property; once no subscription line remains, every tagged
 * line is removed. Runs site-wide so a subscription dropped from the cart
 * drawer or cart page takes its gifts with it, and one added by a cart
 * upgrade brings them.
 *
 * A gift the shopper removes stays out: cart-items writes the variant into
 * the cart's `_declined_gifts` attribute with the removal, and declined
 * variants are skipped here until no subscription line remains (the list is
 * cleared along with the gifts).
 *
 * The gifts are a first-subscription perk. For a shopper `Theme.gifts.eligible`
 * rules out (a logged-in customer tagged as a past or present subscriber —
 * see snippets/gift-eligibility) nothing is added, and any gift line already
 * in the cart, say from before they logged in, is removed: on load as
 * well as on every change.
 *
 * The free-shipping half of the offer is an automatic discount in admin
 * (subscription purchases, first payment only) — nothing to do here.
 *
 * Each gift is one unit. A gift line above one — left by an earlier race,
 * or a cart edited elsewhere — is cleared and the gift added back once: the
 * gift discount covers a single unit, so the second would be charged.
 *
 * Each sync waits its turn in the cart mutation queue (shared with the
 * cart's plan-tier moves) and reads the cart only once its turn comes. The
 * cart the triggering event carried can't stand in for it: a sync queued
 * behind another (two quick adds) would see the gifts the first one adds
 * as missing and add them a second time.
 */
class GiftSync {
  /** True while a sync is queued but not yet started */
  #queued = false;

  constructor() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    // A cart carried over from before the shopper logged in may still hold
    // gifts they no longer qualify for, and one from before the fix a gift
    // held twice (a gift row that isn't --single): set either right without
    // waiting for a change
    const giftRow = document.querySelector('.cart-items__table-row--gift');
    const doubledGift = document.querySelector('.cart-items__table-row--gift:not(.cart-items__table-row--single)');
    if ((!this.#eligible && giftRow) || doubledGift) this.#sync();
  }

  /** False for a shopper the first-order gifts are closed to (see scripts.liquid) */
  get #eligible() {
    return Theme.gifts?.eligible !== false;
  }

  /** @param {Event} event */
  #handleCartLinesUpdate = (event) => {
    const promise = /** @type {CustomEvent & { promise?: Promise<{ detail?: { source?: string } }> }} */ (event).promise;
    // Waits for the change to land, so the sync reads the cart it left
    promise
      ?.then(({ detail }) => {
        if (detail?.source !== 'gift-sync') this.#sync();
      })
      .catch(() => {});
  };

  /** Gift variant ids: the theme settings' and the current page's buy box's */
  get #wantedVariantIds() {
    const host = document.querySelector('pdp-buy-box-component[data-gift-variants]');
    const fromPage = host instanceof HTMLElement ? (host.dataset.giftVariants ?? '').split(',') : [];
    const fromSettings = Theme.gifts?.variants ?? [];
    const ids = [...fromSettings, ...fromPage].map((id) => String(id).trim()).filter(Boolean);
    return [...new Set(ids)];
  }

  get #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  /**
   * Queues a sync. A sync already waiting absorbs the new trigger: it reads
   * the cart when it starts, so it sees this change too. One running lets
   * this one queue behind it.
   */
  #sync() {
    if (this.#queued) return;
    this.#queued = true;
    enqueueCartMutation(() => {
      this.#queued = false;
      return this.#run();
    });
  }

  /**
   * The declined gift variant ids a cart carries.
   *
   * @param {Record<string, unknown> | undefined} attributes
   * @returns {string[]}
   */
  #declinedIds(attributes) {
    return String(attributes?.[DECLINED_ATTRIBUTE] ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  async #run() {
    try {
      const cart = await this.#fetchCart();
      // A shopper the gifts are closed to reads as unsubscribed here: nothing
      // is added, and any gift line in the cart goes
      const hasSubscription = this.#eligible && cart.items.some((item) => item.selling_plan_allocation);
      const giftLines = cart.items.filter((item) => item.properties?._gift);
      const declined = this.#declinedIds(cart.attributes);
      // Declined gifts stay out while their subscription is in the cart; the
      // subscription leaving forgets them
      const clearDeclined = declined.length > 0 && !hasSubscription;
      const cleared = { [DECLINED_ATTRIBUTE]: '' };
      const updateUrl = `${Theme.routes.cart_update_url}.js`;

      /** @type {Array<{ url: string, body: Record<string, unknown> }>} */
      const requests = [];
      /** @type {Array<{ merchandiseId?: string, id?: string, quantity: number }>} */
      const lines = [];
      /** @type {'add' | 'update'} */
      let action = 'update';

      if (hasSubscription) {
        /** @type {Map<string, number>} */
        const giftQuantities = new Map();
        for (const line of giftLines) {
          const id = String(line.variant_id);
          giftQuantities.set(id, (giftQuantities.get(id) ?? 0) + line.quantity);
        }
        // Lines of a gift held more than once go, and the gift is added back
        // once (unless declined) — clearing them all is unambiguous where a
        // gift discount has split the gift into a free and a paid line
        const surplus = giftLines.filter((line) => (giftQuantities.get(String(line.variant_id)) ?? 0) > 1);
        const missing = this.#wantedVariantIds.filter(
          (id) => !declined.includes(id) && (giftQuantities.get(id) ?? 0) !== 1
        );

        if (surplus.length) {
          /** @type {Record<string, number>} */
          const updates = {};
          for (const line of surplus) updates[line.key] = 0;
          lines.push(...surplus.map((line) => ({ id: line.key, quantity: 0 })));
          requests.push({ url: updateUrl, body: { updates } });
        }
        if (missing.length) {
          action = 'add';
          lines.push(...missing.map((id) => ({ merchandiseId: id, quantity: 1 })));
          requests.push({
            url: Theme.routes.cart_add_url,
            body: { items: missing.map((id) => ({ id: Number(id), quantity: 1, properties: { _gift: 'subscription' } })) },
          });
        }
      } else if (giftLines.length || clearDeclined) {
        /** @type {Record<string, number>} */
        const updates = {};
        for (const line of giftLines) updates[line.key] = 0;
        lines.push(...giftLines.map((line) => ({ id: line.key, quantity: 0 })));
        requests.push({
          url: updateUrl,
          body: { ...(giftLines.length ? { updates } : {}), ...(clearDeclined ? { attributes: cleared } : {}) },
        });
      }

      if (!requests.length) return;

      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'cart',
          lines,
          promise: deferred.promise,
        })
      );

      try {
        // Only the last request renders the sections, so the cart paints once
        /** @type {{ sections?: Record<string, string> } | null} */
        let result = null;
        for (const [index, request] of requests.entries()) {
          const body = index === requests.length - 1 ? { ...request.body, sections: this.#cartSectionIds } : request.body;
          result = await this.#post(request.url, body);
        }

        const updatedCart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
          detail: {
            items: updatedCart.items,
            attributes: updatedCart.attributes,
            source: 'gift-sync',
            sections: result?.sections,
          },
        });
      } catch (error) {
        console.error('[gift-sync]', error);
        deferred.reject(error);
      }
    } catch (error) {
      console.error('[gift-sync]', error);
    }
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} body
   * @returns {Promise<any>} The cart endpoint's JSON
   */
  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.status) throw new Error(result.description || result.message || 'Gift sync failed');
    return result;
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }
}

new GiftSync();
